import { useNavigation } from '@react-navigation/native';
import { Meta, Spinner, theme, Title } from 'aimo-ui';
import ParkingContext from 'context/ParkingContext';
import PriceEstimateContext from 'context/PriceEstimateContext';
import React, { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParkingStore } from 'stores/parkingStore';
import styled from 'styled-components/native';
import {
  ColumnWithMargins,
  ScreenContainer,
  StyledContainer,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { ParkNavProps } from 'types/navigation';
import { currencyFormatter } from 'utils/commonUtils';
import ResumeTimeDisplay from './ResumeTimeDisplay';
import { Row } from './styles/styles';

const PillContainer = styled(StyledContainer)`
  flex: 0 1 100%;
  border-radius: 60px;
  padding: 0px 12px;
  align-self: baseline;
  font-weight: 700;
`;

const BoldMeta = styled(Meta)`
  font-family: ${theme.fontFamily.bold};
`;

const ResumeParking = () => {
  const { parkingZoneResult: parkingZone } = useContext(ParkingContext);
  const {
    loadingPrice,
    priceEstimate,
    getPricingEstimateWithSessionId,
    setStartButtonOpacity,
  } = useContext(PriceEstimateContext);

  const { selectedParkingZoneId, endTime } = useParkingStore(
    (state) => state.parkingConditions
  );
  const { navigate: navigatePark } = useNavigation<ParkNavProps>();
  const { t } = useTranslation();
  const { countryCode } = parkingZone?.data?.parkZone || {};
  const resumableSessionInfo = useParkingStore(
    (state) => state.resumableSession
  );

  const memoizedSession = useMemo(
    () => resumableSessionInfo,
    [resumableSessionInfo]
  );

  useEffect(() => {
    if (!memoizedSession) {
      useParkingStore.getState().setResumeSessionPossible(false);
      navigatePark('parkDetail', { parkingZoneUid: selectedParkingZoneId });
    }
  }, [navigatePark, selectedParkingZoneId, memoizedSession]);

  useEffect(() => {
    if (
      endTime &&
      ((selectedParkingZoneId && memoizedSession?.startDate) ||
        memoizedSession?.sessionId)
    ) {
      getPricingEstimateWithSessionId({
        variables: {
          input: {
            sessionId: memoizedSession?.sessionId,
            endDate: endTime ? endTime : new Date(),
          },
        },
      });
      useParkingStore.getState().setResumeSessionPossible(true);
    }
  }, [
    getPricingEstimateWithSessionId,
    endTime,
    selectedParkingZoneId,
    memoizedSession?.startDate,
    memoizedSession?.sessionId,
    setStartButtonOpacity,
  ]);

  return (
    <ScreenContainer>
      <StyledWrapper center maxWidth={310}>
        <Title center>{t('parking.resumeParkingSession.title')}</Title>
        <ColumnWithMargins mt={10}>
          <Meta center>{t('parking.resumeParkingSession.description')}</Meta>
        </ColumnWithMargins>
      </StyledWrapper>
      <StyledWrapper center mb={5}>
        <PillContainer
          color={theme.colors.gray50}
          testID={'resume-parking-price'}>
          <Row>
            <BoldMeta testID={'resume-parking-desc'}>
              {t('parking.resumeParkingSession.price')}
            </BoldMeta>
            {loadingPrice && !priceEstimate ? (
              <Spinner small />
            ) : (
              <BoldMeta
                testID={'resume-parking-price-value'}>{` ${currencyFormatter(
                priceEstimate?.totalAmount || 0,
                countryCode || 'SE'
              )}`}</BoldMeta>
            )}
          </Row>
        </PillContainer>
      </StyledWrapper>
      <StyledWrapper center maxWidth={295} testID={'resume-parking-time'}>
        {memoizedSession && (
          <ResumeTimeDisplay
            spacerCount={5}
            startTime={memoizedSession?.startDate as Date}
            endTime={endTime}
          />
        )}
      </StyledWrapper>
    </ScreenContainer>
  );
};

export default ResumeParking;
